import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface Confetti {
  id: number;
  x: number;
  y: number;
  delay: number;
  size: number;
  color: string;
  rotationSpeed: number;
}

const PASTEL_COLORS = [
  "#FFB3D9", // Pink
  "#FFFACD", // Yellow
  "#B3E5FC", // Blue
  "#C8E6C9", // Green
  "#E1BEE7", // Purple
  "#FFCCBC", // Peach
];

export const EasterConfetti = () => {
  const [confetti, setConfetti] = useState<Confetti[]>([]);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    // Generate confetti with random positions and colors
    const confettiCount = 10 + Math.floor(Math.random() * 3); // 10-12 pieces
    const newConfetti: Confetti[] = [];

    for (let i = 0; i < confettiCount; i++) {
      newConfetti.push({
        id: i,
        x: Math.random() * 100, // 0-100%
        y: Math.random() * 100, // 0-100%
        delay: Math.random() * 2, // 0-2s delay
        size: 6 + Math.random() * 6, // 6-12px
        color: PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)],
        rotationSpeed: 180 + Math.random() * 180, // 180-360 degrees
      });
    }

    setConfetti(newConfetti);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Don't render if user prefers reduced motion
  if (prefersReducedMotion) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      {confetti.map((piece) => (
        <motion.div
          key={piece.id}
          className="absolute rounded-sm"
          style={{
            left: `${piece.x}%`,
            top: `${piece.y}%`,
            width: piece.size,
            height: piece.size,
            backgroundColor: piece.color,
            willChange: "transform, opacity",
          }}
          animate={{
            y: [0, -30, -15, 0, 15, 0], // Floating motion
            rotate: [0, piece.rotationSpeed, piece.rotationSpeed * 2], // Tumbling
            scale: [1, 1.2, 0.8, 1], // Pulsing
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: piece.delay,
          }}
        />
      ))}
    </div>
  );
};
